'use client';

import { useState, useEffect } from 'react';
import {
  XMarkIcon,
  DocumentTextIcon,
  ClipboardDocumentIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { fetcher } from '@utils/data-fetching/fetcher';
import { useTranslation } from 'react-i18next';
import type { ApiSchema } from '@type/api/apiUtilityFn';

interface TranscriptTurn {
  role: 'assistant' | 'user';
  content: string;
}

// API returns nested structure with turns and summary
type TranscriptResponse = ApiSchema<'InterviewTranscriptOutput'>;

interface InterviewTranscriptModalProps {
  isOpen: boolean;
  onClose: () => void;
  interviewId: number;
  candidateName?: string;
}

/**
 * Modal component to display interview transcript
 * Handles new API structure: { transcript, turns: { turns: [[speaker, content]], summary }, interviewId }
 */
export const InterviewTranscriptModal = ({
  isOpen,
  onClose,
  interviewId,
  candidateName,
}: InterviewTranscriptModalProps) => {
  const { t } = useTranslation();
  const [turns, setTurns] = useState<TranscriptTurn[]>([]);
  const [summary, setSummary] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen && interviewId) {
      setIsLoading(true);
      setError(null);

      fetcher<TranscriptResponse>(`/v1/screening/interviews/${interviewId}/transcript`, undefined)
        .then((response) => {
          // Handle nested turns structure: response.turns.turns
          // Check for null/undefined at each level due to nullable types
          const turnsData = response && response.turns ? response.turns : null;

          if (turnsData && turnsData.turns && Array.isArray(turnsData.turns) && turnsData.turns.length > 0) {
            // Convert API tuples [speaker, content] to TranscriptTurn objects
            const parsedTurns: TranscriptTurn[] = turnsData.turns.map((turn) => ({
              role: turn[0] === 'Bestie' ? 'assistant' : 'user',
              content: turn[1],
            }));
            setTurns(parsedTurns);

            // Set summary if available
            if (turnsData.summary) {
              setSummary(turnsData.summary);
            }
          } else {
            setError(t('screening.transcript.error'));
          }
        })
        .catch(() => {
          setError(t('screening.transcript.error'));
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isOpen, interviewId, t]);

  const handleCopyToClipboard = () => {
    const text = turns
      .map((msg) => `${msg.role === 'assistant' ? 'Bestie' : t('screening.candidate.label')}: ${msg.content}`)
      .join('\n\n');
    void navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadAsText = () => {
    const text = turns
      .map((msg) => `${msg.role === 'assistant' ? 'Bestie' : t('screening.candidate.label')}: ${msg.content}`)
      .join('\n\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `interview-transcript-${interviewId}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative z-10 m-4 flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 p-4">
          <div className="flex items-center gap-2">
            <DocumentTextIcon className="size-5 text-slate-600" />
            <div>
              <h2 className="text-lg font-bold text-slate-800">{t('screening.interview.transcript')}</h2>
              {candidateName && (
                <p className="text-sm text-slate-500">
                  {(t('screening.transcript.full.name') || '{candidateName}').replace('{candidateName}', candidateName)}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          >
            <XMarkIcon className="size-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="size-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
            </div>
          )}

          {error && (
            <div className="rounded-lg bg-red-50 p-4 text-center text-red-600">
              {error}
            </div>
          )}

          {!isLoading && !error && turns.length > 0 && (
            <>
              {/* Display summary if available */}
              {summary && (
                <div className="mb-4 rounded-lg bg-blue-50 p-4 border border-blue-200">
                  <h3 className="mb-2 text-sm font-semibold text-blue-900">
                    {t('screening.transcript.summary', 'Interview Summary')}
                  </h3>
                  <p className="text-sm text-blue-800">{summary}</p>
                </div>
              )}

              {/* Display conversation turns */}
              <div className="space-y-4">
                {turns.map((message, index) => (
                  <div
                    key={`transcript-${index}-${message.role}`}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                        message.role === 'assistant'
                          ? 'bg-emerald-100 text-slate-700'
                          : 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      {message.role === 'assistant' && (
                        <div className="mb-1 flex items-center gap-1">
                          <span className="text-lg">🤖</span>
                        </div>
                      )}
                      <p className="text-sm">{message.content}</p>
                    </div>
                    {message.role === 'user' && (
                      <div className="ml-2 flex items-end">
                        <div className="flex size-6 items-center justify-center rounded-full bg-slate-300 text-xs text-slate-600">
                          👤
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {!isLoading && !error && turns.length === 0 && (
            <div className="py-8 text-center text-slate-500">
              {t('screening.transcript.empty')}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 border-t border-slate-200 p-4">
          <button
            type="button"
            onClick={handleCopyToClipboard}
            disabled={turns.length === 0}
            className={`flex flex-1 items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50 ${
              copied
                ? 'border-emerald-500 bg-emerald-50 text-emerald-600'
                : 'border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ClipboardDocumentIcon className="size-4" />
            {copied ? 'Copied!' : 'Copy to Clipboard'}
          </button>
          <button
            type="button"
            onClick={handleDownloadAsText}
            disabled={turns.length === 0}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="size-4" />
            {t('screening.transcript.download.text')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InterviewTranscriptModal;
